import { LABELS_CONFIG, VIEWING_METHODS } from '@/Hooks/PUJ/constants';
import {
  BillingFrequency,
  CustomerPackage,
  CustomerPackageDiscount,
  CustomerSubscribedPkgEventSubscriptionSubscription,
  GetActivePkgsWithDetailsQuery,
  GetSubscribedPackagesQuery,
  Maybe,
  ViewingMethod,
} from '@/lib/graph-ql';
import { RoutePath } from '@/Routes/RoutePath';
import qs from 'query-string';
import { generatePath, useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import { defaultErrorSubTitle, defaultErrorTitle } from '@/Components/Message/ErrorMessage';
import getLabel from '@/Utils/LabelsConfig';
import {
  LinearChannel,
  LinearViewingMethod,
  Movie,
  Show,
  VodViewingMethod,
} from '@skytvnz/sky-app-store/lib/types/graph-ql';
import pluralize from 'pluralize';
import { isNil } from 'ramda';
import React from 'react';
import { isoTimeToSeconds } from './TimeFormat';

type NavigateToPackageParams = {
  offerId: string;
  query?: Record<string, string | number | boolean>;
  history: ReturnType<typeof useHistory>;
  location: ReturnType<typeof useLocation>;
  openModal: (message: any) => void;
};

type ViewingMethods = VodViewingMethod & LinearViewingMethod;

export const getBillingFrequency = (frequency: BillingFrequency | undefined) => {
  if (frequency && frequency === BillingFrequency.Monthly) {
    return '/mth';
  }
  return null;
};
/**
 * Used to get discount price with adding dollar and BillingFrequency sign eg. $10/mth
 */
export const getDiscountPrice = (
  basePrice: number | undefined,
  discount: number | undefined,
  billingFrequency: BillingFrequency | undefined,
  needBillingFrequency = true,
) => {
  let discountPrice = null;

  // Ensure that we calculate the discount price even if billingFrequency is not provided
  if (basePrice && discount !== undefined) {
    if (needBillingFrequency && billingFrequency) {
      const billingFreq = getBillingFrequency(billingFrequency);
      discountPrice = `$${basePrice - discount}${billingFreq}`;
    } else {
      discountPrice = `$${basePrice - discount}`; // Don't include billing frequency
    }
  }

  return discountPrice;
};

// Helper to get discount month
export const getDiscountMonth = (discountDuration?: string) => {
  let discountMonth = null;
  if (discountDuration && !isNil(discountDuration)) {
    discountMonth = Math.round(
      (isoTimeToSeconds(discountDuration) % 31536000) / 2628000,
    ).toString();
  }
  return discountMonth;
};

// Function to check if packages exist
export const getHasPackages = (packages: Maybe<CustomerPackage>[]) => {
  return packages?.length > 0;
};

/**
 * Used to get Base price with adding dollar and BillingFrequency sign eg. $20/mth
 */
export const getBasePrice = (
  basePrice: number | undefined,
  billingFrequency: BillingFrequency | undefined,
  needBillingFrequency = true,
) => {
  let actualPrice = null;
  const billingFreq = getBillingFrequency(billingFrequency);
  if (basePrice && billingFreq) {
    actualPrice = needBillingFrequency ? `$${basePrice}${billingFreq}` : `$${basePrice}`;
  }
  return actualPrice;
};

/**
 * Used to get hint based on passing values
 */
export const getHintMsg = (
  discountPrice: string | null,
  discountMonth: string | null,
  basePrice: number | undefined,
  billingFrequency: BillingFrequency | undefined,
  offerTitle: string | undefined,
  isPackageDetail?: boolean,
) => {
  let hintPackage: string | JSX.Element = <></>;
  const rollingOnMonthly = getLabel(LABELS_CONFIG.ROLLING_MONTHLY_PLAN);
  const billingFreq = getBillingFrequency(billingFrequency);
  if (discountPrice && discountMonth && billingFreq && basePrice) {
    hintPackage = (
      <>
        Add {offerTitle} for <b className="pujDiscount">{discountPrice}</b> for&nbsp;
        {pluralize('month', Number(discountMonth), true)} then ${basePrice}
        {billingFreq} {rollingOnMonthly}
      </>
    );
  } else if (!isNil(offerTitle)) {
    hintPackage = isPackageDetail ? (
      <>
        Add {offerTitle} for&nbsp;
        <b>
          ${basePrice}
          {billingFreq}
        </b>
        &nbsp;{rollingOnMonthly}
      </>
    ) : (
      `You need the ${offerTitle} package to watch this content`
    );
  }

  return hintPackage;
};

// Function to get promo offer details
export const getPromoOffer = (
  discount: Maybe<CustomerPackageDiscount> | undefined,
  billingFrequency: BillingFrequency | undefined,
) => {
  let promo;
  let formattedBillingFrequency: string | undefined;
  if (!isNil(discount?.type) && !isNil(discount?.duration)) {
    const discountDuration = getDiscountMonth(discount?.duration);
    if (billingFrequency === 'MONTHLY') {
      if (discountDuration && Number(discountDuration) > 1) {
        formattedBillingFrequency = 'months';
      } else {
        formattedBillingFrequency = 'month';
      }
    } else {
      formattedBillingFrequency = billingFrequency;
    }
    promo = `${discount?.type} price for ${discountDuration} ${formattedBillingFrequency}`;
  }

  return promo;
};

// Function to handle package upgrades
export const getIsPackageUpgradeRequire = (
  viewingMethods: Maybe<ViewingMethod>[] | undefined,
  isPackageUpgradeRequire: boolean,
  mySubscription: boolean,
) => {
  return viewingMethods && isPackageUpgradeRequire && !mySubscription;
};

// Main functions
export const findOfferInPackages = (customerPackages: Maybe<CustomerPackage>[]) => {
  return customerPackages.find(packageData => packageData?.offer) ?? null;
};

// Function to check if content is valid for Vod or Linear
export const isContentValidForVodOrLinear = (data: ViewingMethods): boolean =>
  (data?.__typename === VIEWING_METHODS.VOD || data?.__typename === VIEWING_METHODS.LINEAR) &&
  getHasPackages(data?.packages);

export const updatePackageCacheData = (
  prev: GetSubscribedPackagesQuery | GetActivePkgsWithDetailsQuery,
  subscriptionData: {
    data: CustomerSubscribedPkgEventSubscriptionSubscription;
  },
) => {
  if (!subscriptionData.data) {
    return prev;
  }
  const account = subscriptionData.data?.customerAccount?.account;
  return {
    ...prev,
    experience: {
      ...prev.experience,
      customer: {
        ...prev.experience.customer,
        packages: account?.packages,
      },
    },
  };
};

export const getOfferIdFromLinearViewingMethod = (
  item?: LinearChannel | undefined,
): string | null => {
  if (item && item.viewingMethods) {
    return (
      item?.viewingMethods?.flatMap(viewingMethod => {
        const VMethod = viewingMethod as LinearViewingMethod;
        if (VMethod?.__typename === VIEWING_METHODS.LINEAR) {
          return VMethod.packages?.map(packageItem => packageItem?.offer?.id).filter(Boolean);
        }
        return [];
      })?.[0] || null
    );
  }
  return null;
};

export const getOfferIdFromViewingMethod = (viewingMethods, targetMethodType) => {
  const match = viewingMethods.find(method => method?.__typename === targetMethodType);

  if (!match || !Array.isArray(match.packages)) return null;

  const packageWithOffer = match.packages.find(pkg => pkg?.offer?.id);

  return packageWithOffer?.offer?.id ?? null;
};

const getPackageIdsRequiredToPlay = (content?: LinearChannel | Movie | Show | '') => {
  const listOfPackageRequired: string[] = [];
  content?.viewingMethods?.forEach((viewingMethod: Maybe<ViewingMethods>) => {
    if (
      viewingMethod &&
      viewingMethod.__typename &&
      ['VodViewingMethod', 'LinearViewingMethod'].includes(viewingMethod.__typename)
    ) {
      viewingMethod.packages?.forEach((packageItem: Maybe<CustomerPackage>) => {
        if (packageItem) {
          listOfPackageRequired.push(packageItem.id);
        }
      });
    }
  });
  return listOfPackageRequired;
};

export const shouldRefetchForPackageChange = (
  content: LinearChannel | Movie | Show,
  customerPackagesIds: string[],
  previousCustomerPackagesIds: string[],
): boolean => {
  // Packages required for the content to play/record
  const requiredPackageIds = getPackageIdsRequiredToPlay(content);
  // Find packages that were added(not present in previous)
  const addedPackages = customerPackagesIds.filter(
    pkg => !previousCustomerPackagesIds.includes(pkg),
  );
  // Find packages that were removed (not present in previous)
  const removedPackages = previousCustomerPackagesIds.filter(
    pkg => !customerPackagesIds.includes(pkg),
  );
  const hasRelevantChange =
    // Case1:At least 1 added package is in the required set
    (addedPackages.some(pkg => requiredPackageIds.includes(pkg)) ||
      // Case2:At least 1 removed package is in the required set
      removedPackages.some(pkg => requiredPackageIds.includes(pkg))) &&
    // Compare previous customerpackeges with currentpackage
    JSON.stringify([...customerPackagesIds].sort()) !==
      JSON.stringify([...previousCustomerPackagesIds].sort());

  return hasRelevantChange;
};

const navigateToPackage = ({
  offerId,
  query = {},
  history,
  location,
  openModal,
}: NavigateToPackageParams) => {
  if (!offerId) {
    openModal({
      title: defaultErrorTitle,
      content: defaultErrorSubTitle,
      onOk: () => {},
    });
    return;
  }
  // Set up the params for the path
  const params = { offerId };

  // Generate the path with query parameters
  const path = qs.stringifyUrl({
    url: generatePath(RoutePath.package, params),
    query,
  });

  // Push the generated path into history with the origin as state
  history.push(path, { origin: location.pathname });
};

export default navigateToPackage;
